import type { GraphQLErrorPayload as GraphQLErrorInterop } from '@confluence/relay-errors/entry-points/RelayError.ts';

import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { expVal, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';

import EndOfPageRecommendationQuery from './__generated__/EndOfPageRecommendationQuery.graphql.ts';
import EndOfPageRecommendationTwgQuery from './__generated__/EndOfPageRecommendationTwgQuery.graphql.ts';
import {
	MAX_TWG_RECS,
	buildTwgQueryContext,
	buildTwgContentAri,
	buildTwgRecsCypher,
	isTwgSupportedContentType,
} from './twgQueryConfig.ts';

type PreloadResult = {
	data: unknown;
	errors?: ReadonlyArray<GraphQLErrorInterop>;
	hasErrors: boolean;
};

const NOOP_RESULT: PreloadResult = { data: undefined, hasErrors: false };

export function preloadEndOfPageRecommendation({
	contentId,
	spaceKey,
	contentType,
	cloudId,
}: {
	contentId: any;
	spaceKey: any;
	contentType: any;
	cloudId?: string;
}): Promise<PreloadResult> {
	// Treatment preloads the TWG query (not cc-smarts) to prime the cache for the
	// client consumers. NO-EXPOSURE reader: this runs during SSR for every view-page
	// load, so exposure is logged at the loader instead (not here).
	if (UNSAFE_expValEqualsNoExposure('confluence_eop_recs_twg_source_exp', 'isEnabled', true)) {
		// Variables must be byte-identical to the client hook for the cache-first hit.
		if (!isTwgSupportedContentType(contentType) || !cloudId || !contentId) {
			return Promise.resolve(NOOP_RESULT);
		}
		return queryAgg({
			query: EndOfPageRecommendationTwgQuery,
			variables: {
				cypherQuery: buildTwgRecsCypher(buildTwgContentAri(cloudId, contentId, contentType)),
				first: MAX_TWG_RECS,
				key: spaceKey,
				keyString: spaceKey,
			},
			context: { headers: { 'X-Query-Context': buildTwgQueryContext(cloudId) } },
		});
	}

	return query({
		query: EndOfPageRecommendationQuery,
		variables: {
			type: contentType,
			id: contentId,
			exp: expVal('cc_ai_discovery_linked_pages_on_related_pages', 'experience', 'end_of_page_v5'),
			key: spaceKey,
			keyString: spaceKey,
		},
	});
}
